'use client';

import React from 'react';
import { Sparkles, X } from 'lucide-react';
import { AnimatePresence, m } from 'motion/react';
import { css } from 'styled-system/css';

import type { Text } from '@shaojiang/content';
import { DesignVariants } from '@shaojiang/designs/runtime/DesignOnly';

import { Copy } from './Copy';

interface DesignHintProps {
  show: boolean;
  text: Text;
  dismissLabel: string;
  onDismiss: () => void;
}

const card = css({
  position: 'absolute',
  top: 'calc(100% + 14px)',
  right: '-6px',
  zIndex: 'dropdown',
  display: 'flex',
  alignItems: 'center',
  gap: '2',
  w: 'max-content',
  maxW: '72vw',
  pl: '4',
  pr: '1.5',
  py: '2',
  borderRadius: 'media',
  bg: 'tone.sun.bg',
  color: 'tone.sun.fg',
  boxShadow: 'button',
  // a yellow chip gets a yellow sticker shadow (see designs/kit/sticker-shadow)
  _storybook: {
    boxShadow:
      '0 5px 0 color-mix(in srgb, {colors.sun.400} 75%, transparent), 0 15px 26px -12px {colors.sun.400}',
  },
  transformOrigin: 'top right',
  _nebula: {
    pl: '3',
    borderRadius: 'l3',
    bg: 'bg.raised',
    color: 'fg.default',
    boxShadow: 'raised',
    backdropFilter: 'blur({blurs.glass})',
  },
});

/** The little tail pointing up at the switch */
const tail = css({
  position: 'absolute',
  top: '-6px',
  right: '22px',
  w: '3.5',
  h: '3.5',
  rotate: '45deg',
  borderRadius: '3px',
  bg: 'inherit',
});

/**
 * A one-time nudge under the design switch: a sticker note in Storybook, a glass pill in
 * Nebula. It pops in after a few seconds on a first visit and goes away for good once the
 * visitor opens the switch or dismisses it.
 */
export function DesignHint({ show, text, dismissLabel, onDismiss }: DesignHintProps) {
  return (
    <AnimatePresence>
      {show && (
        <m.div
          role='status'
          className={card}
          initial={{ opacity: 0, y: -8, scale: 0.8, rotate: 0 }}
          animate={{ opacity: 1, y: 0, scale: 1, rotate: -2 }}
          exit={{ opacity: 0, y: -6, scale: 0.9 }}
          transition={{ type: 'spring', stiffness: 420, damping: 22 }}
        >
          <span className={tail} aria-hidden />
          <DesignVariants
            as='span'
            storybook={null}
            nebula={<Sparkles className={css({ w: '4', h: '4', color: 'fg.accent' })} />}
          />
          <span
            className={css({
              textStyle: 'handSm',
              _nebula: { textStyle: 'xs', fontWeight: '500' },
            })}
          >
            <Copy text={text} />
          </span>
          <button
            type='button'
            aria-label={dismissLabel}
            onClick={onDismiss}
            className={css({
              display: 'grid',
              placeItems: 'center',
              w: '7',
              h: '7',
              borderRadius: 'full',
              bg: 'transparent',
              border: 'none',
              color: 'inherit',
              opacity: '0.6',
              cursor: 'pointer',
              _hover: { opacity: '1' },
              '& svg': { w: '3.5', h: '3.5' },
            })}
          >
            <X />
          </button>
        </m.div>
      )}
    </AnimatePresence>
  );
}
